import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { catalogActions } from './actions'
import {
  selectBatteries,
  selectFilters,
  selectPagination,
  selectListLoading,
  selectListError,
  selectBatteryById,
  selectBatteryLoading,
  selectBatteryError,
  selectFeaturedBatteries,
  selectFeaturedLoading
} from './selectors'
import { BatteryFilters } from '../../../types/api'

/**
 * Hook para gerenciar listagem de baterias com filtros
 * Compatível com useBatteries antigo
 */
export const useBatteries = (initialFilters?: BatteryFilters) => {
  const dispatch = useAppDispatch()
  const batteries = useAppSelector(selectBatteries)
  const filters = useAppSelector(selectFilters)
  const pagination = useAppSelector(selectPagination)
  const loading = useAppSelector(selectListLoading)
  const error = useAppSelector(selectListError)

  // Carregar baterias na inicialização
  useEffect(() => {
    const currentFilters = initialFilters ? { ...filters, ...initialFilters } : filters
    dispatch(catalogActions.fetchBatteriesRequest(currentFilters))
  }, []) // Executar apenas uma vez na montagem

  // Atualizar quando initialFilters mudar
  useEffect(() => {
    if (initialFilters) {
      dispatch(catalogActions.updateFilters(initialFilters))
      dispatch(catalogActions.fetchBatteriesRequest({ ...filters, ...initialFilters }))
    }
  }, [JSON.stringify(initialFilters)])

  const updateFilters = useCallback((newFilters: Partial<BatteryFilters>) => {
    dispatch(catalogActions.updateFilters(newFilters))
    dispatch(catalogActions.fetchBatteriesRequest({ ...filters, ...newFilters }))
  }, [dispatch, filters])

  const goToPage = useCallback((page: number) => {
    dispatch(catalogActions.updateFilters({ page }))
    dispatch(catalogActions.fetchBatteriesRequest({ ...filters, page }))
  }, [dispatch, filters])

  const nextPage = useCallback(() => {
    if (pagination.page < pagination.totalPages) {
      goToPage(pagination.page + 1)
    }
  }, [pagination, goToPage])

  const prevPage = useCallback(() => {
    if (pagination.page > 1) {
      goToPage(pagination.page - 1)
    }
  }, [pagination, goToPage])

  const reload = useCallback(() => {
    dispatch(catalogActions.fetchBatteriesRequest(filters))
  }, [dispatch, filters])

  return {
    batteries,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    goToPage,
    nextPage,
    prevPage,
    reload
  }
}

/**
 * Hook para obter detalhes de uma bateria específica
 * Compatível com useBattery antigo
 */
export const useBattery = (id?: string, slug?: string) => {
  const dispatch = useAppDispatch()
  const battery = useAppSelector((state) => {
    if (id) return selectBatteryById(id)(state)
    if (slug) return selectBatteryById(slug)(state)
    return null
  })
  const loading = useAppSelector(selectBatteryLoading)
  const error = useAppSelector(selectBatteryError)

  useEffect(() => {
    if (id || slug) {
      dispatch(catalogActions.fetchBatteryRequest({ id, slug }))
    }
  }, [dispatch, id, slug])

  return { battery, loading, error }
}

/**
 * Hook para obter baterias em destaque
 * Compatível com useFeaturedBatteries antigo
 */
export const useFeaturedBatteries = () => {
  const dispatch = useAppDispatch()
  const batteries = useAppSelector(selectFeaturedBatteries)
  const loading = useAppSelector(selectFeaturedLoading)
  const error = useAppSelector((state) => state.catalog.featuredError)

  useEffect(() => {
    dispatch(catalogActions.fetchFeaturedBatteriesRequest())
  }, [dispatch])

  return { batteries, loading, error }
}

